/* ==========================================================================
   C700K — Global interactions
   Scroll reveal, sticky/transparent header, mobile menu, headline reveal
   ========================================================================== */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.addEventListener('DOMContentLoaded', function () {
    initScrollReveal();
    initStickyHeader();
    initMobileMenu();
    initHeadlineSplit();
    initFullscreenQuoteLines();
    initImpactNumbers();
    initEcosystem();
    initHorizontalGalleries();
  });

  /* ------------------------------------------------------------------
     Scroll reveal — fades/slides elements in once as they enter view
     ------------------------------------------------------------------ */
  function initScrollReveal() {
    var items = document.querySelectorAll('.reveal');
    if (!items.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    items.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------
     Sticky header — transparent over hero, solid once scrolled
     ------------------------------------------------------------------ */
  function initStickyHeader() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;

    var threshold = 80;

    function update() {
      if (window.scrollY > threshold) {
        header.classList.add('is-solid');
      } else {
        header.classList.remove('is-solid');
      }
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ------------------------------------------------------------------
     Mobile menu toggle
     ------------------------------------------------------------------ */
  function initMobileMenu() {
    var toggle = document.querySelector('[data-menu-toggle]');
    var menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;

    toggle.addEventListener('click', function () {
      var isOpen = menu.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });

    menu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        menu.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      });
    });
  }

  /* ------------------------------------------------------------------
     Headline split reveal — the hero headline reveals word by word
     ------------------------------------------------------------------ */
  function initHeadlineSplit() {
    var headline = document.querySelector('[data-split-reveal]');
    if (!headline || prefersReducedMotion) return;

    var text = headline.textContent.trim();
    var words = text.split(' ');

    headline.textContent = '';
    headline.setAttribute('aria-label', text);

    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'split-word';
      span.style.setProperty('--word-index', i);
      span.textContent = word;
      headline.appendChild(span);
      if (i < words.length - 1) {
        headline.appendChild(document.createTextNode(' '));
      }
    });

    requestAnimationFrame(function () {
      headline.classList.add('is-split-ready');
    });
  }

  /* ------------------------------------------------------------------
     Fullscreen quote — lines fade in sequentially as the section enters
     ------------------------------------------------------------------ */
  function initFullscreenQuoteLines() {
    var lines = document.querySelectorAll('[data-fq-line]');
    if (!lines.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      lines.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.6 }
    );

    lines.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------
     Impact numbers — counts up from 0 once visible
     ------------------------------------------------------------------ */
  function initImpactNumbers() {
    var numbers = document.querySelectorAll('[data-count-to]');
    if (!numbers.length) return;

    function animateCount(el) {
      var target = parseFloat(el.getAttribute('data-count-to'));
      var suffix = el.getAttribute('data-count-suffix') || '';
      var decimals = el.getAttribute('data-count-decimals') ? parseInt(el.getAttribute('data-count-decimals'), 10) : 0;
      var duration = 1600;
      var start = null;

      if (prefersReducedMotion) {
        el.textContent = target.toFixed(decimals) + suffix;
        return;
      }

      function step(timestamp) {
        if (!start) start = timestamp;
        var progress = Math.min((timestamp - start) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = target * eased;
        el.textContent = current.toFixed(decimals) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    if (!('IntersectionObserver' in window)) {
      numbers.forEach(animateCount);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateCount(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    numbers.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------
     Company ecosystem — subtle pointer-follow drift on floating nodes
     ------------------------------------------------------------------ */
  function initEcosystem() {
    var stage = document.querySelector('[data-ecosystem]');
    if (!stage || prefersReducedMotion) return;

    var nodes = stage.querySelectorAll('[data-ecosystem-node]');

    stage.addEventListener('pointermove', function (e) {
      var rect = stage.getBoundingClientRect();
      var relX = (e.clientX - rect.left) / rect.width - 0.5;
      var relY = (e.clientY - rect.top) / rect.height - 0.5;

      nodes.forEach(function (node) {
        var depth = parseFloat(node.getAttribute('data-depth')) || 1;
        var x = relX * 18 * depth;
        var y = relY * 18 * depth;
        node.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
      });
    });

    stage.addEventListener('pointerleave', function () {
      nodes.forEach(function (node) { node.style.transform = ''; });
    });
  }

  /* ------------------------------------------------------------------
     Horizontal galleries — drag / wheel to scroll sideways
     ------------------------------------------------------------------ */
  function initHorizontalGalleries() {
    var tracks = document.querySelectorAll('[data-horizontal-track]');
    if (!tracks.length) return;

    tracks.forEach(function (track) {
      var isDown = false;
      var startX, scrollLeft;

      track.addEventListener('wheel', function (e) {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          track.scrollLeft += e.deltaY;
          e.preventDefault();
        }
      }, { passive: false });

      track.addEventListener('pointerdown', function (e) {
        isDown = true;
        track.classList.add('is-dragging');
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
      });
      ['pointerup', 'pointerleave'].forEach(function (evt) {
        track.addEventListener(evt, function () {
          isDown = false;
          track.classList.remove('is-dragging');
        });
      });
      track.addEventListener('pointermove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - track.offsetLeft;
        track.scrollLeft = scrollLeft - (x - startX) * 1.4;
      });
    });
  }
})();
